import { useState, useEffect } from "react";
import { Tooltip } from "react-tooltip";
import Container from "../../components/Container";
import FloatInput from "../../components/FloatInput";

const PATH = process.env.REACT_APP_ROOF_PATH_TO_BACKEND;

// Функция для проверки, является ли параметр максимальной длиной листа
function isMaxSheetLengthParam(paramName) {
  return paramName && paramName.toLowerCase().includes("максимальная длина листа");
}

function Dimensions({
  typeRoof,
  typeOptions,
  onParameterChange,
  selectedNomenclature,
  setSum,
  sum
}) {
  const currentRoof = typeOptions.find((roof) => roof.value === typeRoof);
  const [isHovered, setIsHovered] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [warning, setWarning] = useState("");

  // Функция валидации для вальмовой кровли
  const validateHipRoof = () => {
    if (typeRoof === "hip") {
      const params = currentRoof?.parameters;
      const w1 = parseFloat(params?.find(p => p.name.includes("Ширина ската 1 (W1)"))?.value) || 0;
      const w3 = parseFloat(params?.find(p => p.name.includes("Ширина конька (W3)"))?.value) || 0;
      const h2 = parseFloat(params?.find(p => p.name.includes("Длина ската 2 (H2)"))?.value) || 0;

      if (w3 + h2 > w1 && w1 !== 0 && w3 !== 0 && h2 !== 0) {
        return "Ошибка: Ширина конька (W3) + Длина ската 2 (H2) не могут быть больше Ширины ската 1 (W1)";
      }
    }
    return "";
  };

  const fetchRoofParameters = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${PATH}/api/parameters`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ typeRoof, parameters: currentRoof?.parameters, selectedNomenclature }),
      });

      if (!response.ok) {
        throw new Error(`Ошибка HTTP: ${response.status}`);
      }

      const data = await response.json();
      const sum = data.reduce((total, currentValue) => {
        total = total + currentValue.area;
        return total;
      }, 0);
      setSum(sum);
      setTableData(data);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleShowTable = () => {
    // Проверка на заполненность всех полей
    if (
      !selectedNomenclature ||
      selectedNomenclature === "" ||
      currentRoof?.parameters?.some(param => param.value === "" || param.value === null)
    ) {
      setWarning("Необходимо заполнить все поля");
      setShowTable(false);
      return;
    }

    // Валидация для вальмовой кровли
    const validationError = validateHipRoof();
    if (validationError) {
      setWarning(validationError);
      setShowTable(false);
      return;
    }

    setWarning("");
    setShowTable(true);
    fetchRoofParameters();
  };

  useEffect(() => {
    // Проверяем, что все поля заполнены и валидация пройдена
    if (
      selectedNomenclature &&
      currentRoof?.parameters?.every(param => param.value !== "" && param.value !== null)
    ) {
      const validationError = validateHipRoof();
      if (validationError) {
        setWarning(validationError);
        setShowTable(false);
      } else {
        setWarning("");
        fetchRoofParameters();
      }
    } else {
      setShowTable(false);
    }
  }, [typeOptions, selectedNomenclature, currentRoof?.parameters]);

  return (
    <Container>
      <h2 className="fs-3 mb-4 text-start fst-italic text-secondary">
        Размеры вашей кровли
      </h2>

      <div className="row align-items-center justify-content-center">
        {/* Левая часть - Таблица параметров */}
        <div className="col-lg-6 col-md-12 mb-3">
          <table className="table table-bordered">
            <thead className="table-light">
              <tr>
                <th>Параметр</th>
                <th>Значение</th>
              </tr>
            </thead>
            <tbody>
              {currentRoof?.parameters?.length ? (
                currentRoof.parameters.map((param, index) => (
                  <tr key={index}>
                    <td>{param.name}</td>
                    <td>
                      <FloatInput
                        value={param.value}
                        onChange={(value) =>
                          onParameterChange(typeRoof, index, value)
                        }
                        placeholder="Введите число..."
                        isSheetLength={isMaxSheetLengthParam(param.name)}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="2" className="text-center text-muted">
                    Нет данных для данной кровли
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Кнопка для показа таблицы */}
          <button 
            className="btn btn-primary mt-3"
            onClick={handleShowTable}
            disabled={loading}
          >
            Показать раскладку металлочерепицы
          </button>

          {/* Отображение предупреждений и ошибок */}
          {(warning || error) && (
            <div className="mt-3">
              {warning && <p className="text-danger">{warning}</p>}
              {error && <p className="text-danger">Ошибка: {error}</p>}
            </div>
          )}

          {/* Таблица с данными */}
          {showTable && tableData && (
            <div className="mt-4">
              <table className="table table-bordered">
                <thead className="table-light">
                  <tr>
                    <th>Длина, м</th>
                    <th>Кол-во, шт</th>
                    <th>Площадь, м2</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.length ? (
                    tableData.map((item, index) => (
                      <tr key={index}>
                        <td>{item.length}</td>
                        <td>{item.count}</td>
                        <td>{item.area}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" className="text-center text-muted">Нет данных</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Правая часть - Картинка */}
        <div className="col-lg-6 col-md-12 d-flex flex-column align-items-center">
          {currentRoof?.imageBig ? (
            <img
              src={currentRoof.imageBig}
              alt={currentRoof.label}
              className="img-fluid mb-3"
              style={{ maxWidth: "100%", height: "auto", borderRadius: "10px" }}
            />
          ) : (
            <p className="text-muted">Изображение отсутствует</p>
          )}
        </div>
      </div>
    </Container>
  );
}

export default Dimensions;